/**
 * The ONE UNTIL semantics.
 *
 * The platform previously had two divergent readers of the same token: the
 * client materialised a DATE-only UNTIL at 23:59:59 UTC while the server's
 * published-feed filter read 00:00:00 UTC — a full day of disagreement on
 * the same stored rule. This module supersedes both.
 *
 * Semantics (a documented FIAS NORMALIZATION POLICY, not a claim about RFC
 * 5545 — the RFC pairs UNTIL's form with DTSTART's and leaves the mixed
 * cases underspecified; providers disagree in practice):
 *
 *  - DATE-TIME with `Z` (`20261231T235959Z`) → exactly that instant.
 *  - Floating DATE-TIME (`20261231T235959`) → that wall time in the
 *    supplied zone.
 *  - DATE-only (`20261231`) → the END of that calendar day IN THE SUPPLIED
 *    ZONE (the user-meaning reading: "repeats until Dec 31" includes
 *    Dec 31's occurrence), i.e. 23:59:59.999 local converted to an instant.
 */

import { parseRecurrenceText } from './text';
import { fromFloating, hasFixedOffset } from './zone';

const UNTIL_TOKEN = /(?:^|;)UNTIL=([^;]+)/i;
const UNTIL_FORM = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})(Z)?)?$/;

/** Extracts the raw UNTIL token from a bare RRULE VALUE, or null. */
export function rawUntilToken(rruleValue: string): string | null {
  const m = UNTIL_TOKEN.exec(rruleValue);
  return m ? m[1].trim() : null;
}

/**
 * Resolves a raw UNTIL token to an instant (ms since epoch) under the
 * policy above. Returns null for a malformed token.
 */
export function untilTokenToMs(token: string, zone: string): number | null {
  const m = UNTIL_FORM.exec(token.trim());
  if (!m) return null;
  const [, y, mo, d, hh, mm, ss, z] = m;

  if (hh !== undefined && z) {
    // DATE-TIME, UTC.
    return Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm), Number(ss));
  }

  // Second precision, matching RRULE UNTIL's own resolution (and the zone
  // converter's convergence granularity).
  const wall =
    hh !== undefined
      ? new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm), Number(ss)))
      : new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), 23, 59, 59));

  if (hasFixedOffset(zone)) return wall.getTime();
  return fromFloating(wall, zone).getTime();
}

/**
 * The series' UNTIL as an instant, reading the stored recurrence text (bare
 * or multi-line) through the shared parser. Null when there is no UNTIL or
 * the value is unreadable.
 */
export function untilInstantMs(recurrenceText: string | null, zone: string): number | null {
  const parsed = parseRecurrenceText(recurrenceText);
  if (!parsed) return null;
  const token = rawUntilToken(parsed.rrule);
  if (!token) return null;
  return untilTokenToMs(token, zone);
}
